import keyBy from 'lodash/keyBy';
import chunk from 'lodash/chunk';
import type { ProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import { runInBatch } from '@atlassian/jira-polaris-lib-run-in-batch/src/index.tsx';
import { logSafeErrorWithoutCustomerDataWrapper } from '@atlassian/jira-polaris-lib-errors/src/common/utils/index.tsx';
import { experience } from '@atlassian/jira-polaris-lib-analytics/src/common/constants/experience/index.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/index.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { performGetIssueTypesForProjects } from '../../services/jira/fetch-issue-types.tsx';
import type { State } from '../types.tsx';
import { transformIssueTypeResponse } from './utils.tsx';

const PROJECT_IDS_LIMIT = 50;
const BATCH_SIZE = 5;

export const fetchIssueTypesForProjects =
	(projectIds: ProjectId[]) =>
	async ({ setState, getState }: StoreActionApi<State>) => {
		experience.issueTypes.issueTypesForProjectsLoad.start();
		const prevState = getState();

		try {
			setState({
				issueTypesByProjectId: {
					...prevState.issueTypesByProjectId,
					...projectIds.reduce<Record<ProjectId, string[]>>((acc, projectId) => {
						acc[projectId] = [];
						return acc;
					}, {}),
				},
			});

			log.safeInfoWithoutCustomerData(
				'polaris.issue-types.actions.fetch-issue-types-for-projects',
				'Fetching issue types for projects',
				{ projectIdsCount: projectIds.length },
			);

			const projectIdsChunks = chunk(projectIds, PROJECT_IDS_LIMIT);
			const batchResponse = await runInBatch(
				projectIdsChunks.map((ids) => () => performGetIssueTypesForProjects(ids)),
				BATCH_SIZE,
			);
			const projects = batchResponse.map((c) => c.projects).flat();

			const { issueTypesById, issueTypesByProjectId } = projects.reduce<
				Pick<State, 'issueTypesById' | 'issueTypesByProjectId'>
			>(
				(acc, { projectId, issueTypes }) => ({
					issueTypesById: {
						...acc.issueTypesById,
						...keyBy(issueTypes.map(transformIssueTypeResponse), ({ id }) => id),
					},
					issueTypesByProjectId: {
						...acc.issueTypesByProjectId,
						[projectId]: issueTypes.map(({ id }) => String(id)),
					},
				}),
				{
					issueTypesById: {},
					issueTypesByProjectId: {},
				},
			);

			setState({
				issueTypesById: {
					...getState().issueTypesById,
					...issueTypesById,
				},
				issueTypesByProjectId: {
					...getState().issueTypesByProjectId,
					...issueTypesByProjectId,
				},
			});

			experience.issueTypes.issueTypesForProjectsLoad.success();
		} catch (maybeError) {
			const error =
				maybeError instanceof Error
					? maybeError
					: new Error('Error while fetching issue types for projects');

			if (isClientFetchError(error)) {
				experience.issueTypes.issueTypesForProjectsLoad.abort();
			} else {
				experience.issueTypes.issueTypesForProjectsLoad.failure(error);
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.error.controllers.issue-types.actions.fetch-issue-types-for-projects',
				'Failed to fetch issue types for projects',
				error,
			);

			throw error;
		}
	};
